import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, FlexContainer, Modal, SimpleTooltip } from '@placer-ui/components';
import { SupportEmailInput } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-email-input/support-email-input';
import { ControlledToggleButton } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-toggle-button';
import type { SupportInputType } from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import type {
    RequestMarketFormProps,
    RequestMarketFormValues,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-market/types';
import { TitleModalHeader } from 'features/offline-reports/support-requests/components/support-requests-modals/components/title-modal-header/title-modal-header';
import { SupportRequestModalContent } from 'features/offline-reports/support-requests/components/support-requests-modals/components/location-modal-content/support-request-modal-content';
import { buildCSV, createCsvData } from 'utils/create-csv/create-csv';
import { supportApi } from 'API/offline-reports-api';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { useSendSupportSplunkEvent } from 'features/offline-reports/support-requests/hooks/use-send-support-splunk-event';
import { ManualRequestMarket } from './manual-request-market';
import { FileRequestMarket } from './file-request-market';
import { getFeatureFlags } from 'core/flow-control';
import { generateCsvFileName } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/utils';
import { useOpenSupportRequest } from 'features/offline-reports/support-requests/components/support-requests-modals/support-modal-manager';
import styles from './request-market.module.scss';

const marketFormConfig = {
    testId: 'request-market',
    title: 'Custom Markets',
    subTitle: 'Request Custom Polygon',
};

const marketTypeOptions = [
    { value: 'file', label: 'Upload File' },
    { value: 'manual', label: 'Enter Markets Details' },
];

const Forms: Record<SupportInputType, React.FC<RequestMarketFormProps>> = {
    file: FileRequestMarket,
    manual: ManualRequestMarket,
};

const defaultValues: RequestMarketFormValues = {
    inputType: 'file',
    files: [],
    markets: [
        {
            name: '',
        },
    ],
    executiveEmail: undefined,
    opportunityUrl: undefined,
};

export const RequestMarket = () => {
    const { getSupportRequests } = useOfflineReportsActions();
    const sendSplunkEvent = useSendSupportSplunkEvent();
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();
    const { openSupportRequest } = useOpenSupportRequest();

    const formController = useForm<RequestMarketFormValues>({
        defaultValues: {
            ...defaultValues,
            inputType: enable_request_properties_sales_flow_ff ? 'manual' : 'file',
        },
        mode: 'onSubmit',
    });

    const inputType = formController.watch('inputType');
    const FormSection = Forms[inputType];

    const handleFormSubmit = async (data: RequestMarketFormValues) => {
        try {
            const fileName = generateCsvFileName({ name: 'Markets_List', withTimeFormat: true });
            const files: File[] = [];

            if (data.inputType === 'manual' && 'markets' in data) {
                const csv = buildCSV(
                    ['name', 'category', 'state', 'city', 'county'],
                    data.markets.map(({ name }) => [
                        name,
                        data.category,
                        data.state,
                        data.city,
                        data.county,
                    ]),
                );
                files.push(new File([createCsvData(csv)], fileName));
            } else if (data.inputType === 'file' && 'files' in data) {
                data.files[0] && files.push(data.files[0].file);
            }

            await supportApi.submitRequest({
                type: 'market',
                email: data.email,
                files,
                information: 'information' in data ? data.information : undefined,
                unmatchedProperties:
                    data.inputType === 'manual' && 'markets' in data
                        ? data.markets.length
                        : undefined,
                executiveEmail: data.executiveEmail,
                opportunityUrl: data.opportunityUrl,
            });

            sendSplunkEvent({
                action: 'submit-support-request',
                value: 'market',
            });
            getSupportRequests();
            openSupportRequest({ flowType: 'success' });
        } catch (e) {
            console.error(e);
            openSupportRequest({ flowType: 'failed', retryType: 'market' });
        }
    };

    return (
        <FormProvider {...formController}>
            <TitleModalHeader
                title={marketFormConfig?.title}
                subTitle={marketFormConfig?.subTitle}
            />
            <SupportRequestModalContent testId={marketFormConfig.testId} requestModalType="market">
                <div className={styles.requestMarket}>
                    {!enable_request_properties_sales_flow_ff && (
                        <FlexContainer gap={16} flexDirection="column" margin="0 0 8px 0">
                            <ControlledToggleButton
                                control={formController.control}
                                name="inputType"
                                testId="request-market-toggle"
                                size="default"
                                fullWidth
                                items={marketTypeOptions}
                            />
                            <SupportEmailInput />
                        </FlexContainer>
                    )}
                    <FormSection formControl={formController} />
                </div>
            </SupportRequestModalContent>
            <Modal.Footer>
                <Modal.Footer.RightControls>
                    <Button
                        testId="request-property-back"
                        variant="secondary-blue"
                        fullWidth
                        onClick={() => openSupportRequest({ flowType: 'typeSelection' })}
                    >
                        Back
                    </Button>
                    <SimpleTooltip
                        appendToParent
                        title="Please fill all mandatory fields to proceed."
                        isActive={!formController.formState.isValid}
                    >
                        <Button
                            testId="request-property-next"
                            variant="primary-brand"
                            isLoading={formController.formState.isSubmitting}
                            disabled={
                                !formController.formState.isValid ||
                                formController.formState.isSubmitting
                            }
                            fullWidth
                            onClick={formController.handleSubmit(handleFormSubmit)}
                        >
                            Submit
                        </Button>
                    </SimpleTooltip>
                </Modal.Footer.RightControls>
            </Modal.Footer>
        </FormProvider>
    );
};
